import { Component, computed, effect, inject, input, output, signal, OnDestroy, AfterViewInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { API_CONFIG, InputFieldComponent, GenericDropdownComponent } from '@zayan-erp/shared';
import { AgencyRealtorService } from '../../services/agency-realtor.service';
import { CityService } from 'libs/home-building/src/lib/services/city.service';
import { selectCountries } from '@zayan-erp/stores';
import * as HomeBuildingActions from '@zayan-erp/stores';
import { RealtorAgency } from '../../models/agency-realtor.models';
import { rxResourceFixed } from '@zayan-erp/shared';

@Component({
  selector: 'lib-realtor-form',
  imports: [ReactiveFormsModule, InputFieldComponent, GenericDropdownComponent],
  templateUrl: './realtor-form.component.html'
})
export class RealtorFormComponent implements AfterViewInit, OnDestroy {
  #store = inject(Store);
  #apiConfig = inject(API_CONFIG);
  #agencyRealtorService = inject(AgencyRealtorService);
  #cityService = inject(CityService);

  enableRecaptcha = input<boolean>(false);
  onSubmit = input.required<Observable<void>>();

  reload = output<boolean>();

  recaptchaWidgetId = signal<number | null>(null);
  private readonly recaptchaSiteKey = this.#apiConfig.recaptchaSiteKey;
  private readonly appName = this.#apiConfig.appName;
  isPublic = input<'public' | null>(null);

  realtorForm = new FormGroup({
    brokerageName: new FormControl<string | null>(null, [Validators.required]),
    agentFirstName: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(10)]),
    agentLastName: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(10)]),
    phone: new FormControl<string | null>(null, [Validators.required]),
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
    // cityId: new FormControl<string | null>(null, [Validators.required]),
    address: new FormGroup({
      countryId: new FormControl<string | null>(null),
      provinceId: new FormControl<string | null>(null),
      cityId: new FormControl<string | null>(null, [Validators.required])
    }),
    captchaToken: new FormControl<string | null>(null)
  });

  countries = this.#store.selectSignal(selectCountries);
  // agencies = toSignal(this.#agencyRealtorService.getAgencies(), { initialValue: [] as RealtorAgency[] });
  // agenciesDisplayList = computed(() =>
  //   (this.agencies() ?? []).map(a => ({
  //     id: a.id,
  //     firstLabel: a.name,
  //     secondLabel: a.code ?? ''
  //   }))
  // );

  selectedCountryId = toSignal(this.realtorForm.get('address.countryId')!.valueChanges, {
    initialValue: this.realtorForm.get('address.countryId')?.value ?? null
  });
  selectedProvinceId = toSignal(this.realtorForm.get('address.provinceId')!.valueChanges, {
    initialValue: this.realtorForm.get('address.provinceId')?.value ?? null
  });

  provincesByCountryId = rxResourceFixed({
    params: () => ({ countryId: this.selectedCountryId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.countryId) {
        return this.#cityService.readProvinces(params.countryId);
      }
      return of([]);
    }
  });
  citiesByProvinceId = rxResourceFixed({
    params: () => ({ provinceId: this.selectedProvinceId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.provinceId) {
        return this.#cityService.readCitiesByProvinceId(params.provinceId);
      }
      return of([]);
    }
  });
  provincesByCountryIdDisplayList = computed(() => this.provincesByCountryId.value() ?? []);
  citiesByProvinceIdDisplayList = computed(() => this.citiesByProvinceId.value() ?? []);

  constructor() {
    this.realtorForm.get('address.countryId')?.valueChanges.subscribe((countryId: string | null) => {
      this.realtorForm.get('address.provinceId')?.setValue(null);
      this.realtorForm.get('address.cityId')?.setValue(null);
      if (countryId) {
        this.#store.dispatch(HomeBuildingActions.loadCitiesByCountryId({ countryId }));
      }
    });
    this.realtorForm.get('address.provinceId')?.valueChanges.subscribe(() => {
      this.realtorForm.get('address.cityId')?.setValue(null);
    });

    if (this.appName === 'public') {
      this.realtorForm.get('captchaToken')?.addValidators([Validators.required]);
    }
    if (this.isPublic() === 'public') {
      this.#store.dispatch(HomeBuildingActions.loadCountries());
    }
  }

  ngAfterViewInit(): void {
    if (this.enableRecaptcha()) {
      setTimeout(() => {
        this.initializeRecaptcha();
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    if (this.recaptchaWidgetId() !== null) {
      window.grecaptcha?.reset(this.recaptchaWidgetId()!);
    }
  }

  saveEffect = effect(() => {
    this.onSubmit().subscribe(() => {
      this.submitRealtorForm();
    });
  });

  private initializeRecaptcha(): void {
    if (typeof window !== 'undefined' && window.grecaptcha) {
      window.grecaptcha.ready(() => {
        const widgetId = window.grecaptcha.render('recaptcha-container', {
          sitekey: this.recaptchaSiteKey,
          size: 'normal',
          callback: (response: string) => {
            this.realtorForm.get('captchaToken')?.setValue(response);
          },
          'expired-callback': () => {
            this.realtorForm.get('captchaToken')?.setValue(null);
          },
          'error-callback': () => {
            this.realtorForm.get('captchaToken')?.setValue(null);
            alert('Error in reCAPTCHA');
          }
        });
        this.recaptchaWidgetId.set(widgetId);
      });
    }
  }

  private validateRecaptcha(): boolean {
    if (!this.enableRecaptcha()) {
      return true;
    }
    const response = this.realtorForm.get('captchaToken')?.value;
    return typeof response === 'string' && response.length > 0;
  }

  private resetRecaptcha(): void {
    if (this.enableRecaptcha() && this.recaptchaWidgetId() !== null) {
      window.grecaptcha?.reset(this.recaptchaWidgetId()!);
      this.realtorForm.get('captchaToken')?.setValue(null);
    }
  }

  submitRealtorForm(): void {
    if (this.enableRecaptcha() && !this.validateRecaptcha()) {
      this.realtorForm.markAllAsTouched();
      alert('Please complete the reCAPTCHA verification');
      return;
    }

    if (this.realtorForm.invalid) {
      this.realtorForm.markAllAsTouched();
      return;
    }

    const formData = this.realtorForm.getRawValue();

    const payload = {
      brokerageName: formData.brokerageName!,
      agentFirstName: formData.agentFirstName!,
      agentLastName: formData.agentLastName!,
      phone: formData.phone!,
      email: formData.email!,
      cityId: formData.address.cityId!,
      captchaToken: formData.captchaToken ?? undefined
    };

    this.#agencyRealtorService.createPublicRealtorRegistration(payload).subscribe({
      next: () => {
        this.realtorForm.reset();
        this.resetRecaptcha();
        this.reload.emit(true);
      }
    });
  }
}
